import { XCircle } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PaymentPinSplitLayout from '../components/Payment/PaymentPinSplitLayout';
import { DEFAULT_CREDIT_CHECKOUT_PLAN } from '../components/CreditSystem/creditCheckoutUtils';
import {
  clearPaymentFailed,
  goToCheckoutAfterPaymentFailure,
  readPaymentFailed,
} from '../utils/creditCheckoutNavigation';
import { clearPaymentFlowFlags } from '../utils/paymentReturnBootstrap';
import { SEO } from '../shared/components/SEO';
import { getAppOrigin } from '../shared/seo/siteOrigin';

interface PaymentFailedPageProps {
  onNavigate?: (view: string) => void;
}

const PaymentFailedPage: React.FC<PaymentFailedPageProps> = () => {
  const { t } = useTranslation();

  const stored = useMemo(() => readPaymentFailed(), []);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  // Mark this page as actively rendered so AppRouter guards don't bounce to '/'
  // after clearPaymentFlowFlags() removes localStorage flags.
  useEffect(() => {
    try { sessionStorage.setItem('ggj_payment_page_active', '/payment-failed'); } catch { /* */ }
    return () => {
      try { sessionStorage.removeItem('ggj_payment_page_active'); } catch { /* */ }
    };
  }, []);

  const planId = stored?.planId || params.get('plan') || DEFAULT_CREDIT_CHECKOUT_PLAN;
  const isRecurring = stored?.recurring || params.get('type') === 'recurring';
  const subscriptionId = stored?.subscriptionId || params.get('subscription_id') || '';
  const orderId = stored?.orderId || params.get('order_id') || '';
  const referenceId = isRecurring ? subscriptionId : orderId;
  const reason = stored?.reason
    || t('views.paymentFailed.defaultReason', {
      defaultValue: 'Your payment could not be completed. No money was deducted.',
    });

  useEffect(() => {
    try {
      clearPaymentFlowFlags();
    } catch {
      /* best-effort */
    }
  }, []);

  const handleRetry = () => {
    clearPaymentFailed();
    goToCheckoutAfterPaymentFailure(planId);
  };

  const handleDashboard = () => {
    clearPaymentFailed();
    try {
      clearPaymentFlowFlags();
    } catch {
      /* best-effort */
    }
    window.location.replace(`${getAppOrigin()}/dashboard`);
  };

  return (
    <>
    <SEO
      title="Payment Failed — GoGrabJob"
      description="Your GoGrabJob payment could not be completed. No charge was made — retry checkout when ready."
      canonical="https://www.gograbjob.com/payment-failed"
      noIndex
    />
    <PaymentPinSplitLayout
      coverVariant="failed"
      coverTitle={t('views.paymentSuccess.coverHeadlineFailed', { defaultValue: "Let's try that again" })}
      coverSubtitle={t('views.paymentSuccess.coverSubtextFailed', {
        defaultValue: "No charge was completed. Return to checkout and retry when you're ready.",
      })}
      onBack={handleRetry}
      backLabel={t('views.paymentSuccess.backToCheckout', { defaultValue: 'Back to checkout' })}
    >
      <div className="payment-success-status">
        <div className="payment-success-body">
          <div className="payment-success-hero-icon payment-success-hero-icon--failed">
            <XCircle className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2.5} aria-hidden />
          </div>
          <h2 className="payment-success-title text-lg sm:text-xl mb-2">
            {t('views.paymentSuccess.paymentFailed')}
          </h2>
          <p className="payment-success-muted mb-4 sm:mb-6 text-sm sm:text-base">{reason}</p>

          {referenceId && (
            <div className="payment-success-receipt payment-success-receipt--glass payment-success-receipt--reference">
              <h3 className="payment-success-receipt-heading">
                {t('views.paymentFailed.detailsTitle', { defaultValue: 'Reference' })}
              </h3>
              <div className="payment-success-receipt-rows">
                <div className="payment-success-receipt-row">
                  <span className="payment-success-receipt-label">
                    {isRecurring
                      ? t('views.paymentFailed.subscriptionId', { defaultValue: 'Subscription ID' })
                      : t('views.paymentSuccess.orderId')}
                  </span>
                  <span className="payment-success-receipt-value payment-success-receipt-value--mono" title={referenceId}>
                    {referenceId.substring(0, 6)}…{referenceId.slice(-4)}
                  </span>
                </div>
                <div className="payment-success-receipt-row">
                  <span className="payment-success-receipt-label">{t('views.paymentSuccess.status')}</span>
                  <span className="payment-success-receipt-value">
                    <span className="payment-success-receipt-pill payment-success-receipt-pill--failed">
                      {t('views.paymentSuccess.statusFailed')}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="payment-success-actions-stack">
            <button
              type="button"
              onClick={handleRetry}
              className="payment-success-cta payment-success-cta--primary"
            >
              {isRecurring
                ? t('views.paymentFailed.retryWithUPI', { defaultValue: '↻ Retry with UPI AutoPay' })
                : t('views.paymentFailed.retryPayment', { defaultValue: '↻ Retry Payment' })}
            </button>
            <button
              type="button"
              onClick={handleRetry}
              className="payment-success-cta payment-success-cta-secondary"
            >
              {t('views.paymentFailed.payWithCard', { defaultValue: '💳 Pay via Credit / Debit Card' })}
            </button>
            <button
              type="button"
              onClick={handleDashboard}
              className="payment-success-cta payment-success-cta-secondary"
              style={{ opacity: 0.7 }}
            >
              {t('views.paymentSuccess.goToDashboard')}
            </button>
          </div>

          <div className="payment-success-receipt payment-success-receipt--glass mt-4" style={{ borderLeft: '3px solid #f59e0b' }}>
            <div className="space-y-1.5 text-xs sm:text-sm" style={{ color: 'var(--ds-text-body, #475569)' }}>
              <p className="font-semibold">{t('views.paymentFailed.whyItFailed', { defaultValue: 'Common reasons:' })}</p>
              <p>• Bank mandate authorization declined or timed out</p>
              <p>• UPI app was closed before completing authorization</p>
              <p>• Insufficient balance or daily transaction limit reached</p>
              <p className="font-medium pt-1">
                {t('views.paymentFailed.noCharge', { defaultValue: '💡 No money was deducted. Your plan selection is saved — just retry.' })}
              </p>
            </div>
          </div>

          <p className="payment-success-muted text-xs sm:text-sm mt-4 leading-relaxed">
            {t('views.paymentFailed.supportHint', {
              defaultValue: 'If money was deducted, it will be auto-refunded within 5–7 business days. Contact support@gograbjob.com if you need help.',
            })}
          </p>
        </div>
      </div>
    </PaymentPinSplitLayout>
    </>
  );
};

export default PaymentFailedPage;
